import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { useState } from "react";
import { Control, useWatch } from "react-hook-form";
import { POWER_UNIT_TYPES_OPTIONs, WEIGHT_UNIT_TYPES_OPTIONs } from "../../config/optionsConstants";
import { validateMeasurementField, valueGraterThanZero } from "../../utils/utils";
import { Card, CardContent, CardHeader } from "../ui/common/card";
import { FormUnitInput } from "../ui/Input/FormUnitInput/FormUnitInput";

interface MeasurementDetailsProps {
  control: Control<any>;
  lockStatus?: boolean;
}

const MeasurementDetails = ({ control, lockStatus = false }: MeasurementDetailsProps) => {
  const [showMeasurements, setShowMeasurements] = useState(true);
  const sideStreamValue = useWatch({ control, name: "sideStream" });
  return (
    <div className="tw-mt-2">
      <Card
        className={`tw-border-border tw-p-0 tw-gap-0 ${lockStatus ? "tw-opacity-85 tw-pointer-events-none" : ""}`}
      >
        <CardHeader
          className="tw-bg-secondary/50 tw-py-3.5 tw-px-3.5 tw-rounded-t-lg tw-cursor-pointer"
          onClick={() => setShowMeasurements(!showMeasurements)}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-w-full">
            <p className="tw-text-sm tw-font-semibold tw-text-foreground">Measurement Details</p>
            {showMeasurements ? (
              <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground tw-shrink-0" />
            ) : (
              <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground tw-shrink-0" />
            )}
          </div>
        </CardHeader>
        <CardContent className="tw-px-3.5 tw-py-4">
          {showMeasurements && (
            <div className="tw-space-y-4">
              <div className="tw-grid tw-w-full tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                <FormUnitInput
                  control={control}
                  name={"capacity"}
                  label="Capacity (input/hr)"
                  unitName="capacityUnit"
                  unitOptions={WEIGHT_UNIT_TYPES_OPTIONs}
                  placeholder="0.00"
                  errorMessage="Capacity is required"
                  enableLimitedDecimal
                  validate={{
                    valueCheck: (v: string | number) =>
                      valueGraterThanZero(v) || "Capacity must be greater than 0",
                    maxCheck: (v: string | number) => validateMeasurementField(v, false)
                  }}
                />
                <FormUnitInput
                  control={control}
                  name={"input"}
                  label="Input"
                  unitName="inputUnit"
                  unitOptions={WEIGHT_UNIT_TYPES_OPTIONs}
                  placeholder="0.00"
                  required
                  errorMessage="Input is required"
                  enableLimitedDecimal
                  validate={{
                    valueCheck: (v: string | number) =>
                      valueGraterThanZero(v) || "Input must be greater than 0",
                    maxCheck: (v: string | number) => validateMeasurementField(v, false)
                  }}
                />
                <FormUnitInput
                  control={control}
                  name={"output"}
                  label="Output"
                  unitName="outputUnit"
                  unitOptions={WEIGHT_UNIT_TYPES_OPTIONs}
                  placeholder="0.00"
                  required
                  errorMessage="Output is required"
                  enableLimitedDecimal
                  validate={{
                    valueCheck: (v: string | number) =>
                      valueGraterThanZero(v) || "Output must be greater than 0",
                    maxCheck: (v: string | number) => validateMeasurementField(v, false)
                  }}
                />
                <div className="tw-flex tw-flex-col">
                  <FormUnitInput
                    control={control}
                    name={"sideStream"}
                    label="Bio waste/side stream"
                    unitName="sideStreamUnit"
                    unitOptions={WEIGHT_UNIT_TYPES_OPTIONs}
                    placeholder="0.00"
                    errorMessage="Bio waste/side stream is required"
                    enableLimitedDecimal
                    validate={{
                      valueCheck: (v: string | number) => validateMeasurementField(v)
                    }}
                  />
                  {sideStreamValue !== "" && (
                    <p className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-primary tw-font-medium tw-relative tw-mt-1">
                      <Info className="tw-h-3 tw-w-3" /> Specify details in the waste and side stream
                      section.
                    </p>
                  )}
                </div>
              </div>
              <div className="tw-grid tw-w-full tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                <FormUnitInput
                  control={control}
                  name={"electricity"}
                  label="Electricity (per input amount)"
                  unitName="electricityUnit"
                  unitOptions={POWER_UNIT_TYPES_OPTIONs}
                  placeholder="0.00"
                  errorMessage="Electricity is required"
                  enableLimitedDecimal
                  validate={{
                    valueCheck: (v: string | number) => validateMeasurementField(v)
                  }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MeasurementDetails;
